import React, {  useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import {
  EditRounded,
  DeleteOutlineRounded,
  PlaylistAddRounded,
} from "@mui/icons-material";


// ---------------------------------------------------------------------------
// Reusable Form Dialog
// ---------------------------------------------------------------------------
function useDialogState(initial = false) {
  const [open, setOpen] = useState(initial);
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  return { open, onOpen, onClose };
}


// ---------------------------------------------------------------------------
// Lists Panel (named lists with items)
// ---------------------------------------------------------------------------
export default function ListsPanel({ lists, onAddList, onRenameList, onDeleteList, onAddItem, onToggleItem, onDeleteItem }) {
  const dlg = useDialogState();
  const [listName, setListName] = useState("");

  function submitNewList() {
    const n = listName.trim();
    if (!n) return;
  
    setListName("");
    dlg.onClose();
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Lists</Typography>
        <Button variant="contained" startIcon={<PlaylistAddRounded />} onClick={dlg.onOpen}>
          New list
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {lists.map((list) => (
          <Grid key={list.id} size={{xs:12, md:6, lg:4}}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1">{list.name}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Rename">
                      <IconButton size="small" onClick={() => {
                        const v = prompt("Rename list", list.name);
                        if (v && v.trim()) onRenameList({...list, name: v.trim()});
                      }}>
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete list">
                      <IconButton size="small" onClick={() => onDeleteList(list)}>
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <List sx={{ pt: 0 }}>
                  {list.items.map((it) => (
                    <ListItem key={it.id} disableGutters secondaryAction={
                      <Tooltip title="Delete item">
                        <IconButton edge="end" size="small" onClick={() => onDeleteItem({...it, list_id: list.id})}>
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }>
                      <ListItemIcon>
                        <Checkbox edge="start" checked={it.done} onChange={() => onToggleItem({...it, 
                                                                            list_id: list.id, 
                                                                            done: !it.done})} />
                      </ListItemIcon>
                      <ListItemText
                        primary={it.text}
                        sx={{ textDecoration: it.done ? "line-through" : "none", opacity: it.done ? 0.6 : 1 }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Stack direction="row" spacing={1}>
                  <TextField size="small" placeholder="New item…" fullWidth
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = e.currentTarget.value.trim();
                        if (v) {
                          onAddItem({text:v, list_id: list.id});
                          e.currentTarget.value = "";
                        }
                      }
                    }}
                  />
                  <Button onClick={(e) => {
                    const parent = e.currentTarget.parentElement.querySelector('input');
                    const v = parent?.value?.trim();
                    if (v) { onAddItem({text:v, list_id: list.id}); parent.value = ""; }
                  }}>Add</Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dlg.open} onClose={dlg.onClose} fullWidth maxWidth="xs">
        <DialogTitle>New list</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="List name" fullWidth value={listName} onChange={(e) => setListName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={dlg.onClose}>Cancel</Button>
          <Button variant="contained" onClick={()=>{submitNewList();
              onAddList({name: listName});}
          }>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
